import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn, mapFictoanCardProps } from "@/lib/utils";
import { FictoanCardProps } from "@/lib/fictoan-schema";

const cardVariants = cva("setu:rounded-lg setu:!border setu:bg-card setu:text-card-foreground setu:shadow-xs", {
    variants: {
        variant: {
            default: "",
            elevated: "setu:!shadow-md",
            outlined: "setu:!border-2",
        },
    },
    defaultVariants: {
        variant: "default",
    },
});

// Combined interface for both shadcn/ui and Fictoan props
interface CardProps
    extends React.HTMLAttributes<HTMLDivElement>,
        VariantProps<typeof cardVariants>,
        Partial<FictoanCardProps> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
    (
        {
            className,
            variant,
            children,
            // Extract Fictoan props
            bgColor,
            bgColour,
            textColor,
            textColour,
            borderColor,
            borderColour,
            padding,
            margin,
            shadow,
            shape,
            classNames,
            ...props
        },
        ref
    ) => {
        // Map Fictoan props using our standardized mapping
        const { className: mappedClassName } = mapFictoanCardProps({
            bgColor,
            bgColour,
            textColor,
            textColour,
            borderColor,
            borderColour,
            padding,
            margin,
            shadow,
            shape,
            classNames,
            children,
        });

        // Determine variant based on Fictoan shadow prop if not explicitly set
        let finalVariant = variant;
        if (!variant && shadow) {
            if (shadow === "soft" || shadow === "hard") {
                finalVariant = "elevated";
            }
        }

        return (
            <div
                ref={ref}
                className={cn(cardVariants({ variant: finalVariant }), mappedClassName, className)}
                {...props}
            >
                {children}
            </div>
        );
    }
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} className={cn("setu:flex setu:flex-col setu:space-y-1.5 setu:p-6", className)} {...props} />
    )
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
    ({ className, ...props }, ref) => (
        <h3
            ref={ref}
            className={cn("setu:text-2xl setu:font-semibold setu:leading-none setu:tracking-tight", className)}
            {...props}
        />
    )
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
    ({ className, ...props }, ref) => (
        <p ref={ref} className={cn("setu:text-sm setu:text-muted-foreground", className)} {...props} />
    )
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => <div ref={ref} className={cn("setu:p-6 setu:pt-0", className)} {...props} />
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div ref={ref} className={cn("setu:flex setu:items-center setu:p-6 setu:pt-0", className)} {...props} />
    )
);
CardFooter.displayName = "CardFooter";

// Fictoan compatibility wrapper
const FictoanCard = React.forwardRef<HTMLDivElement, FictoanCardProps>((props, ref) => {
    const { className, children } = mapFictoanCardProps(props);
    return (
        <Card ref={ref} className={className}>
            {children}
        </Card>
    );
});
FictoanCard.displayName = "FictoanCard";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, FictoanCard };
export type { CardProps };
